import {
  Alert,
  Box,
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useForm, type FormErrors } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconPackages, IconPlus } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { isSelectablePanel } from "../../utils/controlStainSelection";
import { useAccessioningPanelOptions } from "../../hooks/useAccessioningPanels";
import { useCreatePathologistPanelOrder } from "../../hooks/usePanelWorkflow";
import { getPanelWorkflowErrorMessage } from "../../services/reflexPanels.service";
import type { AdditionalStainRequestSurface } from "../../services/stainRequest.service";
import {
  blockTargetKey,
  parseBlockTargetKey,
  type StagedBlockDraft,
} from "../../services/stagedBlockRequest";
import { createUuid } from "../../utils/createUuid";
import { scrollToFirstError } from "../../utils/formValidation";

interface PanelRequestSpecimen {
  id: string;
  number: string;
  blocks?: Array<{ id: string; number: string }>;
}

interface PathologistPanelRequestFormProps {
  caseId: string;
  specimens: PanelRequestSpecimen[];
  preselectedSpecimenId?: string;
  onCancel: () => void;
  onSuccess: () => void;
  surface?: AdditionalStainRequestSurface;
  stagedBlocks?: StagedBlockDraft[];
  selectedTargetKeys?: string[];
  onTargetKeysChange?: (targetKeys: string[]) => void;
  onAddBlocks?: () => void;
}

interface PanelRequestFormValues {
  specimenId: string | null;
  panelId: string | null;
}

interface VisibleBlockTarget {
  key: string;
  label: string;
}

function validateSpecimen(value: string | null): string | null {
  return value ? null : "Select a specimen";
}

function validatePanel(value: string | null): string | null {
  return value ? null : "Select a panel";
}

function getExistingTargetKeys(
  specimen: PanelRequestSpecimen | undefined
): string[] {
  return (specimen?.blocks || []).map((block) =>
    blockTargetKey(specimen?.id || "", { kind: "existing", blockId: block.id })
  );
}

function getSelectedTargetIds(targetKeys: string[]) {
  return targetKeys
    .map((targetKey) => ({ targetKey, target: parseBlockTargetKey(targetKey) }))
    .filter((entry) => entry.target !== null);
}

export default function PathologistPanelRequestForm({
  caseId,
  specimens,
  preselectedSpecimenId,
  onCancel,
  onSuccess,
  surface = "pathologist-review",
  stagedBlocks = [],
  selectedTargetKeys,
  onTargetKeysChange,
  onAddBlocks,
}: PathologistPanelRequestFormProps) {
  const optionsQuery = useAccessioningPanelOptions();
  const mutation = useCreatePathologistPanelOrder(surface);
  const [submitError, setSubmitError] = useState("");
  const specimensWithTargets = useMemo(
    () =>
      specimens.filter(
        (specimen) =>
          Boolean(specimen.blocks?.length) ||
          stagedBlocks.some((draft) => draft.specimenId === specimen.id)
      ),
    [specimens, stagedBlocks]
  );
  const specimenOptions = useMemo(
    () =>
      specimensWithTargets
        .map((specimen) => ({
          value: specimen.id,
          label: `Specimen ${specimen.number}`,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [specimensWithTargets]
  );
  const panels = useMemo(
    () =>
      (optionsQuery.data || [])
        .filter(
          (option) => option.optionType === "panel" && isSelectablePanel(option)
        )
        .sort((left, right) => left.name.localeCompare(right.name)),
    [optionsQuery.data]
  );
  const panelOptions = useMemo(
    () => panels.map((panel) => ({ value: panel.id, label: panel.name })),
    [panels]
  );
  const initialSpecimenId = useMemo(() => {
    if (
      preselectedSpecimenId &&
      specimensWithTargets.some(
        (specimen) => specimen.id === preselectedSpecimenId
      )
    ) {
      return preselectedSpecimenId;
    }
    return specimensWithTargets.length === 1
      ? specimensWithTargets[0].id
      : null;
  }, [preselectedSpecimenId, specimensWithTargets]);
  const initialTargets = useMemo(
    () =>
      getExistingTargetKeys(
        specimensWithTargets.find(
          (specimen) => specimen.id === initialSpecimenId
        )
      ),
    [initialSpecimenId, specimensWithTargets]
  );
  const [internalTargetKeys, setInternalTargetKeys] = useState(initialTargets);
  const targetKeys = selectedTargetKeys || internalTargetKeys;
  const form = useForm<PanelRequestFormValues>({
    initialValues: { specimenId: initialSpecimenId, panelId: null },
    validateInputOnBlur: true,
    validate: {
      specimenId: validateSpecimen,
      panelId: validatePanel,
    },
  });
  const selectedPanel = useMemo(
    () => panels.find((panel) => panel.id === form.values.panelId),
    [form.values.panelId, panels]
  );
  const selectedSpecimen = useMemo(
    () =>
      specimensWithTargets.find(
        (specimen) => specimen.id === form.values.specimenId
      ),
    [form.values.specimenId, specimensWithTargets]
  );
  const visibleTargets = useMemo<VisibleBlockTarget[]>(() => {
    if (!selectedSpecimen) return [];
    const existing = (selectedSpecimen.blocks || []).map((block) => ({
      key: blockTargetKey(selectedSpecimen.id, {
        kind: "existing",
        blockId: block.id,
      }),
      label: `Block ${block.number}`,
    }));
    const staged = stagedBlocks
      .filter((draft) => draft.specimenId === selectedSpecimen.id)
      .map((draft) => ({
        key: blockTargetKey(selectedSpecimen.id, {
          kind: "staged",
          draftKey: draft.draftKey,
        }),
        label: `Block ${draft.previewNumber} (new)`,
      }));
    return [...existing, ...staged];
  }, [selectedSpecimen, stagedBlocks]);
  const visibleTargetKeys = useMemo(
    () => visibleTargets.map((target) => target.key),
    [visibleTargets]
  );
  const effectiveTargetKeys = useMemo(
    () =>
      targetKeys.filter((targetKey) => visibleTargetKeys.includes(targetKey)),
    [targetKeys, visibleTargetKeys]
  );
  const allBlocksSelected = useMemo(
    () =>
      visibleTargetKeys.length > 0 &&
      visibleTargetKeys.every((targetKey) =>
        effectiveTargetKeys.includes(targetKey)
      ),
    [effectiveTargetKeys, visibleTargetKeys]
  );
  const someBlocksSelected = useMemo(
    () =>
      !allBlocksSelected &&
      visibleTargetKeys.some((targetKey) =>
        effectiveTargetKeys.includes(targetKey)
      ),
    [allBlocksSelected, effectiveTargetKeys, visibleTargetKeys]
  );

  const handleTargetKeysChange = useCallback(
    (nextTargetKeys: string[]) => {
      if (onTargetKeysChange) onTargetKeysChange(nextTargetKeys);
      else setInternalTargetKeys(nextTargetKeys);
    },
    [onTargetKeysChange]
  );
  const handleSpecimenChange = useCallback(
    (specimenId: string | null) => {
      form.setFieldValue("specimenId", specimenId);
      handleTargetKeysChange([]);
    },
    [form, handleTargetKeysChange]
  );
  const handleSelectAllBlocks = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleTargetKeysChange(
        event.currentTarget.checked ? visibleTargetKeys : []
      );
    },
    [handleTargetKeysChange, visibleTargetKeys]
  );
  const handleBlockTargetsChange = useCallback(
    (nextTargetKeys: string[]) => handleTargetKeysChange(nextTargetKeys),
    [handleTargetKeysChange]
  );
  const handleInvalidSubmit = useCallback(
    (errors: FormErrors) => scrollToFirstError(form, errors),
    [form]
  );
  const handleValidSubmit = useCallback(
    async (values: PanelRequestFormValues) => {
      const selectedTargets = getSelectedTargetIds(effectiveTargetKeys);
      if (
        !values.specimenId ||
        !selectedPanel ||
        selectedTargets.length === 0
      ) {
        setSubmitError("Select a panel and at least one block.");
        return;
      }
      setSubmitError("");
      const blockIds = selectedTargets
        .filter((entry) => entry.target?.kind === "existing")
        .map(
          (entry) => entry.target?.kind === "existing" && entry.target.blockId
        )
        .filter((blockId): blockId is string => Boolean(blockId));
      const stagedBlockKeys = selectedTargets
        .filter((entry) => entry.target?.kind === "staged")
        .map(
          (entry) => entry.target?.kind === "staged" && entry.target.draftKey
        )
        .filter((draftKey): draftKey is string => Boolean(draftKey));
      const input = {
        specimenId: values.specimenId,
        blockIds: blockIds.sort(),
        panelDefinitionId: selectedPanel.panelDefinitionId,
        panelDefinitionVersionId: selectedPanel.panelDefinitionVersionId,
        idempotencyKey: `manual-panel:${createUuid()}`,
        ...(stagedBlockKeys.length > 0
          ? { stagedBlockKeys: stagedBlockKeys.sort(), stagedBlocks }
          : {}),
      };
      try {
        await mutation.mutateAsync({
          caseId,
          input,
        });
        notifications.show({
          title: "Panel ordered",
          message: `${selectedPanel.name} was requested for ${
            selectedTargets.length
          } selected block${
            selectedTargets.length === 1 ? "" : "s"
          } through the Additional Stain workflow.`,
          color: "green",
        });
        onSuccess();
      } catch (error) {
        setSubmitError(getPanelWorkflowErrorMessage(error));
      }
    },
    [
      caseId,
      effectiveTargetKeys,
      mutation,
      onSuccess,
      selectedPanel,
      stagedBlocks,
    ]
  );
  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      form.onSubmit(handleValidSubmit, handleInvalidSubmit)(event);
    },
    [form, handleInvalidSubmit, handleValidSubmit]
  );
  const handleRetryOptions = useCallback(() => {
    optionsQuery.refetch();
  }, [optionsQuery]);

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Stack gap="md">
        <Alert color="blue" icon={<IconPackages size={18} />}>
          Choose the exact blocks that need this panel. All panel stains will be
          requested together for the selected blocks through the Additional
          Stain workflow.
        </Alert>
        <Group justify="space-between" align="end">
          <Select
            label="Specimen"
            placeholder="Select specimen"
            data={specimenOptions}
            clearable
            withAsterisk
            value={form.values.specimenId}
            onChange={handleSpecimenChange}
            error={form.errors.specimenId}
          />
          {onAddBlocks && (
            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={onAddBlocks}
              type="button"
            >
              Add blocks
            </Button>
          )}
        </Group>
        {selectedSpecimen && (
          <Paper withBorder p="sm" radius="md">
            <Stack gap="xs">
              <Checkbox
                label={`All blocks for specimen ${selectedSpecimen.number}`}
                checked={allBlocksSelected}
                indeterminate={someBlocksSelected}
                onChange={handleSelectAllBlocks}
              />
              <Checkbox.Group
                value={effectiveTargetKeys}
                onChange={handleBlockTargetsChange}
                error={
                  effectiveTargetKeys.length === 0
                    ? "Select at least one block"
                    : undefined
                }
              >
                <Stack gap="xs" pl="md">
                  {visibleTargets.map((target) => (
                    <Checkbox
                      key={target.key}
                      value={target.key}
                      label={target.label}
                    />
                  ))}
                </Stack>
              </Checkbox.Group>
            </Stack>
          </Paper>
        )}
        <Select
          label="Panel"
          placeholder={
            optionsQuery.isLoading ? "Loading panels..." : "Select panel"
          }
          data={panelOptions}
          disabled={optionsQuery.isLoading || optionsQuery.isError}
          searchable
          clearable
          withAsterisk
          {...form.getInputProps("panelId")}
        />
        {optionsQuery.isError && (
          <Alert
            icon={<IconAlertCircle size={18} />}
            title="Panels could not be loaded"
            color="red"
            variant="light"
            role="alert"
          >
            <Group justify="space-between" align="center">
              <Text size="sm">Check your connection and try again.</Text>
              <Button variant="light" color="red" onClick={handleRetryOptions}>
                Retry
              </Button>
            </Group>
          </Alert>
        )}
        {submitError && (
          <Alert
            icon={<IconAlertCircle size={18} />}
            title="Panel was not ordered"
            color="red"
            variant="light"
            role="alert"
          >
            {submitError}
          </Alert>
        )}
        {selectedPanel && (
          <Text size="sm" c="dimmed">
            {selectedPanel.components
              .map(
                (component) => `${component.stainCode} ×${component.quantity}`
              )
              .join(" · ")}
          </Text>
        )}
        <Group justify="space-between">
          <Button
            variant="default"
            onClick={onCancel}
            disabled={mutation.isPending}
            type="button"
          >
            Back to individual stains
          </Button>
          <Button
            leftSection={<IconPackages size={16} />}
            type="submit"
            loading={mutation.isPending}
          >
            Order panel for selected blocks
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}
